import { Bell, BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { usePushNotifications } from '@/hooks/use-push-notifications';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';

interface PushNotificationSettingsProps {
  compact?: boolean;
}

export function PushNotificationSettings({ compact = false }: PushNotificationSettingsProps) {
  const {
    isSupported,
    permission,
    isEnabled,
    isLoading,
    error,
    enableNotifications,
    disableNotifications,
  } = usePushNotifications();

  if (!isSupported) {
    return null;
  }

  // Compact mode - simple toggle switch (for use within other cards)
  if (compact) {
    return (
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">Push Notifications</p>
            <p className="text-xs text-muted-foreground">
              {permission === 'denied' ? 'Blocked in browser settings' : isEnabled ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled && permission !== 'denied'}
          onCheckedChange={(checked) => {
            if (checked) {
              enableNotifications();
            } else {
              disableNotifications();
            }
          }}
          disabled={isLoading || permission === 'denied'}
        />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Push Notifications</CardTitle>
        <CardDescription>
          Receive real-time notifications when you're not using the app
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {isEnabled ? 'Notifications Enabled' : 'Notifications Disabled'}
            </p>
            <p className="text-sm text-muted-foreground">
              {permission === 'default' &&
                'Click enable to allow push notifications'}
              {permission === 'denied' &&
                'Notifications blocked. Please enable in browser settings.'}
              {permission === 'granted' && isEnabled &&
                'You will receive push notifications'}
              {permission === 'granted' && !isEnabled &&
                'Push notifications are available'}
            </p>
          </div>

          {isEnabled ? (
            <Button
              onClick={disableNotifications}
              disabled={isLoading}
              variant="outline"
              size="sm"
            >
              <BellOff className="mr-2 h-4 w-4" />
              Disable
            </Button>
          ) : (
            <Button
              onClick={enableNotifications}
              disabled={isLoading || permission === 'denied'}
              size="sm"
            >
              <Bell className="mr-2 h-4 w-4" />
              Enable
            </Button>
          )}
        </div>

        {permission === 'denied' && (
          <Alert>
            <AlertDescription>
              To enable push notifications, you need to allow them in your browser settings.
              <br />
              <strong>Chrome/Edge:</strong> Click the lock icon in the address bar → Site Settings → Notifications
              <br />
              <strong>Firefox:</strong> Click the lock icon → Clear permissions and set again
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
